import { StoneColor } from '../types';
import { coordinateToSGF, sgfToCoordinate } from './coordinate';
/**
 * SGF (Smart Game Format) 工具函数
 */
/**
 * 解析SGF字符串
 */
export function parseSGF(sgf) {
    let index = 0;
    const length = sgf.length;
    // 跳过空白字符
    function skipWhitespace() {
        while (index < length && /\s/.test(sgf[index])) {
            index++;
        }
    }
    // 读取属性值
    function readPropertyValue() {
        skipWhitespace();
        if (sgf[index] !== '[') {
            throw new Error(`期望 '[' 在位置 ${index}`);
        }
        index++; // 跳过 '['
        const start = index;
        while (index < length && sgf[index] !== ']') {
            // 处理转义字符
            if (sgf[index] === '\\') {
                index++;
            }
            index++;
        }
        if (index >= length) {
            throw new Error(`未闭合的 '[' 在位置 ${start - 1}`);
        }
        const value = sgf.slice(start, index);
        index++; // 跳过 ']'
        return value;
    }
    // 读取节点
    function readNode() {
        skipWhitespace();
        if (sgf[index] !== ';') {
            throw new Error(`期望 ';' 在位置 ${index}`);
        }
        index++; // 跳过 ';'
        const node = { properties: [] };
        while (index < length && /[A-Z]/.test(sgf[index])) {
            // 读取属性标识符
            const keyStart = index;
            while (index < length && /[A-Z]/.test(sgf[index])) {
                index++;
            }
            const key = sgf.slice(keyStart, index);
            // 读取属性值
            const values = [];
            while (index < length && sgf[index] === '[') {
                values.push(readPropertyValue());
            }
            node.properties.push({ key, values });
        }
        return node;
    }
    // 读取游戏树
    function readGameTree() {
        skipWhitespace();
        if (sgf[index] !== '(') {
            throw new Error(`期望 '(' 在位置 ${index}`);
        }
        index++; // 跳过 '('
        const root = readNode();
        // 读取后续节点：';' 序列为链式父子关系（主变化），'(' 为分支
        let currentNode = root;
        while (index < length && sgf[index] !== ')') {
            if (sgf[index] === '(') {
                // 分支：作为当前节点的额外子节点
                const branch = readGameTree();
                if (!currentNode.children)
                    currentNode.children = [];
                currentNode.children.push(branch);
            }
            else if (sgf[index] === ';') {
                // 序列：下一个节点成为当前节点的子节点，并沿主链前进
                const next = readNode();
                if (!currentNode.children)
                    currentNode.children = [];
                currentNode.children.push(next);
                currentNode = next;
            }
            else {
                skipWhitespace();
            }
        }
        if (index >= length) {
            throw new Error(`未闭合的 '('`);
        }
        index++; // 跳过 ')'
        return root;
    }
    try {
        skipWhitespace();
        if (sgf[index] !== '(') {
            throw new Error('SGF格式错误: 期望以"("开始');
        }
        const gameTree = readGameTree();
        // 提取根节点属性
        const properties = [];
        for (const prop of gameTree.properties) {
            properties.push(prop);
        }
        return {
            properties,
            gameTree
        };
    }
    catch (error) {
        throw new Error(`SGF解析错误: ${error instanceof Error ? error.message : String(error)}`);
    }
}
/**
 * 生成SGF字符串
 */
export function generateSGF(game) {
    let result = '';
    // 生成属性
    function generateProperties(properties) {
        let propsStr = '';
        for (const prop of properties) {
            propsStr += prop.key;
            for (const value of prop.values) {
                propsStr += `[${escapeSGFValue(value)}]`;
            }
        }
        return propsStr;
    }
    // 生成节点
    function generateNode(node) {
        let nodeStr = ';' + generateProperties(node.properties);
        if (node.children && node.children.length > 0) {
            if (node.children.length === 1) {
                // 单个子节点，直接连接
                nodeStr += generateNode(node.children[0]);
            }
            else {
                // 多个子节点，作为分支
                for (const child of node.children) {
                    nodeStr += `(${generateNode(child)})`;
                }
            }
        }
        return nodeStr;
    }
    // 转义SGF值
    function escapeSGFValue(value) {
        return value.replace(/[\\\]]/g, '\\$&');
    }
    result = `(${generateNode(game.gameTree)})`;
    return result;
}
/**
 * 从SGF创建游戏信息
 */
export function createGameFromSGF(sgf) {
    const game = parseSGF(sgf);
    const moves = [];
    // 提取游戏信息
    let boardSize = 19;
    let komi = 7.5;
    let handicap = 0;
    for (const prop of game.properties) {
        switch (prop.key) {
            case 'SZ':
                boardSize = parseInt(prop.values[0], 10);
                break;
            case 'KM':
                komi = parseFloat(prop.values[0]);
                break;
            case 'HA':
                handicap = parseInt(prop.values[0], 10);
                break;
        }
    }
    // 提取棋步
    function extractMoves(node) {
        for (const prop of node.properties) {
            if (prop.key === 'B' || prop.key === 'W') {
                const color = prop.key === 'B' ? StoneColor.BLACK : StoneColor.WHITE;
                const coord = prop.values[0] ? sgfToCoordinate(prop.values[0]) : null;
                moves.push({ color, coord });
            }
        }
        if (node.children && node.children.length > 0) {
            // 只取第一个分支（主变化）
            extractMoves(node.children[0]);
        }
    }
    extractMoves(game.gameTree);
    return {
        boardSize: boardSize,
        komi,
        handicap,
        moves
    };
}
/**
 * 创建SGF字符串
 */
export function createSGF(moves, options = {}) {
    const properties = [
        { key: 'GM', values: ['1'] }, // 围棋
        { key: 'FF', values: ['4'] }, // 格式版本
        { key: 'CA', values: ['UTF-8'] }, // 字符编码
        { key: 'AP', values: ['GoPlatform:1.0'] },
        { key: 'SZ', values: [String(options.boardSize || 19)] },
        { key: 'KM', values: [String(options.komi || 7.5)] },
        { key: 'HA', values: [String(options.handicap || 0)] }
    ];
    if (options.blackName) {
        properties.push({ key: 'PB', values: [options.blackName] });
    }
    if (options.whiteName) {
        properties.push({ key: 'PW', values: [options.whiteName] });
    }
    if (options.date) {
        properties.push({ key: 'DT', values: [options.date] });
    }
    if (options.result) {
        properties.push({ key: 'RE', values: [options.result] });
    }
    // 创建游戏树
    const gameTree = { properties };
    let currentNode = gameTree;
    // 添加棋步
    for (const move of moves) {
        const moveNode = {
            properties: [
                {
                    key: move.color === StoneColor.BLACK ? 'B' : 'W',
                    values: [move.coord ? coordinateToSGF(move.coord) : '']
                }
            ]
        };
        if (!currentNode.children) {
            currentNode.children = [];
        }
        currentNode.children.push(moveNode);
        currentNode = moveNode;
    }
    // 讲解标记挂在最后一个节点（终局讲解标记）
    if (options.marks && options.marks.length > 0) {
        currentNode.properties.push(...marksToProperties(options.marks));
    }
    const sgfGame = {
        properties,
        gameTree
    };
    return generateSGF(sgfGame);
}
/**
 * 简化SGF字符串（移除注释和分支）
 */
export function simplifySGF(sgf) {
    const game = parseSGF(sgf);
    // 移除注释属性
    function removeComments(node) {
        node.properties = node.properties.filter(prop => prop.key !== 'C');
        if (node.children) {
            // 只保留第一个分支
            if (node.children.length > 1) {
                node.children = [node.children[0]];
            }
            for (const child of node.children) {
                removeComments(child);
            }
        }
    }
    removeComments(game.gameTree);
    return generateSGF(game);
}
/**
 * 验证SGF格式
 */
export function validateSGF(sgf) {
    try {
        parseSGF(sgf);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * 提取SGF中的注释
 */
export function extractCommentsFromSGF(sgf) {
    const game = parseSGF(sgf);
    const comments = [];
    let moveNumber = 0;
    function extractFromNode(node) {
        for (const prop of node.properties) {
            if (prop.key === 'B' || prop.key === 'W') {
                moveNumber++;
            }
            else if (prop.key === 'C' && prop.values[0]) {
                comments.push({
                    moveNumber,
                    comment: prop.values[0]
                });
            }
        }
        if (node.children && node.children.length > 0) {
            extractFromNode(node.children[0]);
        }
    }
    extractFromNode(game.gameTree);
    return comments;
}
// ---- 讲解标记读写（写回 SGF 用） ----
// 标记类型 → SGF 属性：circle=CR / triangle=TR / cross=MA / square=SQ / letter=LB
const MARK_KEY = {
    circle: 'CR',
    triangle: 'TR',
    cross: 'MA',
    square: 'SQ',
    letter: 'LB'
};
/** 标记数组 → SGF 属性（按 key 聚合） */
export function marksToProperties(marks) {
    const byKey = {};
    for (const m of marks) {
        const key = MARK_KEY[m.type] || 'MA';
        const coordStr = coordinateToSGF(m.coord);
        const value = m.type === 'letter' ? `${coordStr}:${m.letter || ''}` : coordStr;
        (byKey[key] = byKey[key] || []).push(value);
    }
    return Object.entries(byKey).map(([key, values]) => ({ key, values }));
}
/** 从 SGF 提取所有讲解标记（CR/TR/MA/SQ/LB） */
export function extractMarksFromSGF(sgf) {
    const game = parseSGF(sgf);
    const marks = [];
    function collect(node) {
        for (const prop of node.properties) {
            let type = null;
            if (prop.key === 'CR')
                type = 'circle';
            else if (prop.key === 'TR')
                type = 'triangle';
            else if (prop.key === 'MA')
                type = 'cross';
            else if (prop.key === 'SQ')
                type = 'square';
            else if (prop.key === 'LB')
                type = 'letter';
            if (!type)
                continue;
            for (const value of prop.values) {
                if (type === 'letter') {
                    const idx = value.indexOf(':');
                    const coordStr = idx >= 0 ? value.slice(0, idx) : value;
                    const letter = idx >= 0 ? value.slice(idx + 1) : '';
                    const coord = sgfToCoordinate(coordStr);
                    if (coord)
                        marks.push({ coord, type: 'letter', letter });
                }
                else {
                    const coord = sgfToCoordinate(value);
                    if (coord)
                        marks.push({ coord, type });
                }
            }
        }
        if (node.children) {
            for (const child of node.children)
                collect(child);
        }
    }
    collect(game.gameTree);
    return marks;
}
/**
 * 解析死活题/摆子题 SGF：初始摆子(AB/AW) + 先手(PL) + 标记(TR/CR/SQ/MA)
 * 直接用正则扫描属性（不调用 parseSGF，避开其对纯摆子 SGF 的解析问题）。
 */
export function parseProblem(sgf) {
    const text = String(sgf || '');
    const grab = (key) => {
        const out = [];
        // 允许属性值跨行（MultiGo 导出的 AB/AW 常换行），否则会漏掉换行后的棋子
        const re = new RegExp(key + '((?:\\s*\\[[^\\]]*\\])+)', 'g');
        let m;
        while ((m = re.exec(text)) !== null) {
            const inner = m[1];
            const re2 = /\[([^\]]*)\]/g;
            let m2;
            while ((m2 = re2.exec(inner)) !== null)
                out.push(m2[1]);
        }
        return out;
    };
    const first = (key) => (grab(key)[0] || '');
    const sizeStr = first('SZ');
    const size = sizeStr ? parseInt(sizeStr, 10) : 19;
    const boardSize = (size === 9 || size === 13 ? size : 19);
    const conv = (v) => {
        if (!v || v.length < 2)
            return null;
        const x = v.charCodeAt(0) - 96; // 'a'=1
        const y = v.charCodeAt(1) - 96;
        if (x < 1 || y < 1)
            return null;
        return { x, y };
    };
    const stones = [];
    for (const v of grab('AB')) {
        const c = conv(v);
        if (c)
            stones.push({ color: StoneColor.BLACK, x: c.x, y: c.y });
    }
    for (const v of grab('AW')) {
        const c = conv(v);
        if (c)
            stones.push({ color: StoneColor.WHITE, x: c.x, y: c.y });
    }
    const pl = first('PL');
    const toMove = pl === 'W' ? StoneColor.WHITE : StoneColor.BLACK;
    const marks = [];
    const markMap = [['TR', 'triangle'], ['CR', 'circle'], ['SQ', 'square'], ['MA', 'cross']];
    for (const [k, t] of markMap) {
        for (const v of grab(k)) {
            const c = conv(v);
            if (c)
                marks.push({ coord: c, type: t });
        }
    }
    return { boardSize, stones, toMove, marks };
}
//# sourceMappingURL=sgf.js.map